import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const STORAGE_SESSION = "if_public_chat_session_v1";
const STORAGE_SEEN_NUDGE = "if_public_chat_nudge_v1";
const NUDGE_DELAY_MS = 8000;

// Don't show the widget on these routes — they have their own chat or are auth flows.
const HIDDEN_ROUTES = ["/dashboard", "/admin", "/login", "/signup", "/reset-password"];

const GREETING = {
  role: "assistant",
  content:
    "Hey — I'm Sergeant Credit. Quick question about credit, disputes, or where to start? I've got you.",
  show_book_button: false,
};

export default function PublicChatWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_SESSION) || "");
  const scrollRef = useRef(null);

  // Hide for logged-in customers (dashboard has its own chat) and on private routes.
  const hidden =
    !!user ||
    HIDDEN_ROUTES.some((r) => location.pathname.toLowerCase().startsWith(r));

  // 8-second nudge badge
  useEffect(() => {
    if (hidden) return;
    if (localStorage.getItem(STORAGE_SEEN_NUDGE)) return;
    const t = setTimeout(() => setNudge(true), NUDGE_DELAY_MS);
    return () => clearTimeout(t);
  }, [hidden]);

  // auto-scroll to newest message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const openChat = () => {
    setOpen(true);
    setNudge(false);
    localStorage.setItem(STORAGE_SEEN_NUDGE, "1");
  };

  const closeChat = () => setOpen(false);

  const sendMessage = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;

    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/public-chat", {
        message: text,
        session_id: sessionId || undefined,
      });
      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(STORAGE_SESSION, data.session_id);
      }
      setMessages((m) => [...m, {
        role: "assistant",
        content: data.reply,
        show_book_button: !!data.show_book_button,
      }]);
    } catch (err) {
      const msg = err?.response?.data?.detail || "I couldn't reach the server. Please try again.";
      setMessages((m) => [...m, { role: "assistant", content: msg, show_book_button: false }]);
    } finally {
      setSending(false);
    }
  };

  if (hidden) return null;

  return (
    <>
      {/* Floating bubble (closed state) */}
      {!open && (
        <button
          onClick={openChat}
          className="fixed bottom-5 right-5 z-[110] flex items-center gap-2 group"
          data-testid="public-chat-bubble"
          aria-label="Open chat"
        >
          {nudge && (
            <span className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#2C3413] border border-[#B9C77F]/40 text-xs text-[#F5F4EF] shadow-lg animate-[fadeIn_0.3s]" style={{ fontFamily: "Inter, sans-serif" }}>
              <Sparkles className="w-3.5 h-3.5 text-[var(--if-gold)]" />
              Got credit questions?
            </span>
          )}
          <span className="relative w-14 h-14 rounded-full bg-[var(--if-gold)] text-[#F5F4EF] flex items-center justify-center shadow-2xl transition-transform group-hover:scale-105">
            <MessageCircle className="w-6 h-6" strokeWidth={2.4} />
            {nudge && (
              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 border-2 border-[#F5F4EF] animate-pulse" />
            )}
          </span>
        </button>
      )}

      {/* Chat panel (open state) */}
      {open && (
        <div
          className="fixed bottom-5 right-5 z-[110] w-[calc(100vw-2.5rem)] sm:w-[380px] h-[560px] max-h-[80vh] flex flex-col card-dark border-[var(--if-gold)]/40 shadow-2xl overflow-hidden animate-[fadeIn_0.2s]"
          data-testid="public-chat-panel"
        >
          {/* Header */}
          <div className="inverse flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#2C3413]">
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-full bg-[var(--if-gold)]/15 border border-[var(--if-gold)]/30 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-[var(--if-gold)]" />
              </span>
              <div>
                <div className="font-display text-base text-white leading-tight">Sergeant Credit</div>
                <div className="text-[10px] text-[var(--if-gold)]/80 uppercase tracking-wider" style={{ fontFamily: "Inter, sans-serif" }}>
                  AI Assistant · usually responds instantly
                </div>
              </div>
            </div>
            <button
              onClick={closeChat}
              className="text-white/50 hover:text-white p-1.5 rounded-full hover:bg-white/5 transition-colors"
              data-testid="public-chat-close"
              aria-label="Close chat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-black/40">
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div
                  className={
                    m.role === "user"
                      ? "max-w-[80%] px-3.5 py-2.5 rounded-2xl rounded-br-md bg-[var(--if-gold)] text-[#F5F4EF] text-sm leading-snug"
                      : "max-w-[85%] px-3.5 py-2.5 rounded-2xl rounded-bl-md bg-white/[0.06] border border-white/10 text-white/90 text-sm leading-snug"
                  }
                  style={{ fontFamily: "Inter, sans-serif" }}
                  data-testid={`public-chat-msg-${m.role}`}
                >
                  <div className="whitespace-pre-wrap">{m.content}</div>
                  {m.role === "assistant" && m.show_book_button && (
                    <button
                      onClick={() => { setOpen(false); navigate("/booking"); }}
                      className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[var(--if-gold)] text-[#F5F4EF] text-xs font-bold hover:brightness-110 transition-all"
                      data-testid="public-chat-book-cta"
                    >
                      Book your free 30-min call <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="px-3.5 py-2.5 rounded-2xl rounded-bl-md bg-white/[0.06] border border-white/10 text-white/60 text-sm flex items-center gap-1" data-testid="public-chat-typing">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--if-gold)] animate-pulse" />
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--if-gold)] animate-pulse" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--if-gold)] animate-pulse" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <form onSubmit={sendMessage} className="border-t border-white/10 px-3 py-3 flex items-center gap-2 bg-white">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything about credit…"
              disabled={sending}
              className="flex-1 bg-black/30 border border-white/15 focus:border-[var(--if-gold)]/60 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-white/35 outline-none transition-colors disabled:opacity-50"
              data-testid="public-chat-input"
            />
            <button
              type="submit"
              disabled={!input.trim() || sending}
              className="w-10 h-10 rounded-full bg-[var(--if-gold)] text-[#F5F4EF] flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 transition-all shrink-0"
              data-testid="public-chat-send"
              aria-label="Send"
            >
              <Send className="w-4 h-4" strokeWidth={2.4} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
